// An Orpheus card's alert draft (`card.form_values`, backend ORP-27305) read into the subscription
// shape the wizard hydrates from (v3 `AlertModal.vue`'s `seed = { ...obj, ...draft }`). Over a
// saved alert it fills an edit — the draft carries no WhatsApp numbers, so the saved ones stay —
// and alone it fills a new alert. Read defensively: a field the draft leaves out keeps the base
// value, or the empty value a new alert starts from, so a partial draft can never break hydration.
import type { AlertContentDetailsDto, AlertSubscriptionDto } from '../models/alert.dto'
import type { AlertDraft } from '../models/alert.types'

type Raw = Record<string, unknown>

function isRecord(value: unknown): value is Raw {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function list<T>(value: unknown, fallback: T[]): T[] {
  return Array.isArray(value) ? (value as T[]) : fallback
}

const EMPTY_DETAILS: AlertContentDetailsDto = {
  name: '',
  model: '',
  report_id: 0,
  selected_table_name: '',
  dimensions: [],
  metrics: [],
  alert_window: { type: null as unknown as AlertContentDetailsDto['alert_window']['type'] },
  filters: [],
  thresholds: [],
}

function mergeDetails(base: AlertContentDetailsDto, draft: unknown): AlertContentDetailsDto {
  if (!isRecord(draft)) return base
  const window = isRecord(draft.alert_window)
    ? ({ ...base.alert_window, ...draft.alert_window } as AlertContentDetailsDto['alert_window'])
    : base.alert_window
  return {
    ...base,
    ...(draft as Partial<AlertContentDetailsDto>),
    alert_window: window,
    dimensions: list(draft.dimensions, base.dimensions),
    metrics: list(draft.metrics, base.metrics),
    filters: list(draft.filters, base.filters),
    thresholds: list(draft.thresholds, base.thresholds),
  }
}

export function subscriptionFromFormValues(
  values: Raw,
  base: AlertSubscriptionDto | null,
): AlertSubscriptionDto {
  const seed: AlertSubscriptionDto = base ?? {
    id: 0,
    is_active: true,
    frequency: 'daily',
    channel_type: [],
    allowed_emails: null,
    whatsapp_numbers: null,
    content_details: EMPTY_DETAILS,
  }
  return {
    ...seed,
    channel_type: list(values.channel_type, list(seed.channel_type, [])),
    allowed_emails: list(values.allowed_emails, list(seed.allowed_emails, [])),
    whatsapp_numbers: list(values.whatsapp_numbers, list(seed.whatsapp_numbers, [])),
    content_details: mergeDetails(seed.content_details, values.content_details),
  }
}

/** A listed rule back in the subscription shape the wizard hydrates from. */
export function ruleSubscription(rule: {
  id: number
  isActive: boolean
  channels: AlertDraft['channels']
  recipientEmails: string[]
  whatsappNumbers: string[]
  raw: AlertSubscriptionDto['content_details']
}): AlertSubscriptionDto {
  return {
    id: rule.id,
    is_active: rule.isActive,
    frequency: 'daily',
    channel_type: [...rule.channels],
    allowed_emails: [...rule.recipientEmails],
    whatsapp_numbers: [...rule.whatsappNumbers],
    content_details: rule.raw,
  }
}
